/**
 * Aggregate KPI dashboard — Phase 7F.4.
 * Top KPI cards + funnel + source ROI (with revenue).
 */
import React, { useEffect, useState } from 'react'
import { Stat, Card, Header, WindowSwitch, KeyGate, Loading, ErrView } from './AdminCompliance'
import { AdminThemeToggle, useAdminTheme } from './admin/theme'

const API_URL = process.env.REACT_APP_BACKEND_URL || ''
const NAVY = 'var(--admin-navy)', GOLD = 'var(--admin-gold)', IVORY = 'var(--admin-ivory)', SLATE = 'var(--admin-slate)', MUTED = 'var(--admin-muted)'
const NAVY_SOLID = 'var(--admin-navy-solid)', IVORY_SOLID = 'var(--admin-ivory-solid)', SURFACE = 'var(--admin-surface)', BORDER = 'var(--admin-border)', BORDER_SOFT = 'var(--admin-border-soft)', SCRIM = 'var(--admin-scrim)'

function useAdminKey() {
  const [key, setKey] = useState(() => localStorage.getItem('lwp_admin_key') || '')
  const [input, setInput] = useState('')
  return { key, setKey: (v) => { localStorage.setItem('lwp_admin_key', v); setKey(v) }, input, setInput, reset: () => { localStorage.removeItem('lwp_admin_key'); setKey('') } }
}

export default function AdminAnalytics() {
  useAdminTheme()
  const auth = useAdminKey()
  const [w, setW] = useState('30d')
  const [funnel, setFunnel] = useState(null)
  const [rev, setRev] = useState(null)
  const [comp, setComp] = useState(null)
  const [source, setSource] = useState(null)
  const [err, setErr] = useState(null)
  const [loading, setLoading] = useState(false)

  const load = () => {
    if (!auth.key) return
    setLoading(true); setErr(null)
    const q = `admin_key=${encodeURIComponent(auth.key)}`
    Promise.all([
      fetch(`${API_URL}/api/analytics/funnel?${q}&window=${w}`).then(r => r.json()),
      fetch(`${API_URL}/api/analytics/revenue-projection?${q}&horizon=90d`).then(r => r.json()),
      fetch(`${API_URL}/api/analytics/compliance-velocity?${q}&window=30d`).then(r => r.json()),
      fetch(`${API_URL}/api/analytics/leads-by-source?${q}`).then(r => r.json()),
    ]).then(([f, r, c, s]) => { setFunnel(f); setRev(r); setComp(c); setSource(s) })
      .catch(e => setErr(e.message)).finally(() => setLoading(false))
  }
  useEffect(load, [auth.key, w])

  if (!auth.key) return <KeyGate auth={auth} title="Analytics dashboard" />
  if (loading) return <Loading />
  if (err) return <ErrView err={err} reset={auth.reset} />
  if (!funnel || !rev || !comp) return null

  // Aggregate funnel across all sources
  const totals = { new: 0, contacted: 0, qualified: 0, in_consultation: 0, bound_issued: 0, client: 0 }
  for (const s of funnel.sources) { for (const k of Object.keys(totals)) totals[k] += (s[k] || 0) }
  const maxFunnel = Math.max(1, totals.new || funnel.total_leads_in_window || 1)

  return (
    <div style={{ minHeight: '100vh', background: IVORY, fontFamily: 'Inter,-apple-system,sans-serif', padding: 24, color: SLATE }} data-testid="analytics-dashboard">
      <Header title="Analytics" subtitle={`Window: ${w} · Live from Payload · Refresh on demand`}>
        <WindowSwitch value={w} onChange={setW} options={['30d', '90d', 'ytd']} />
        <AdminThemeToggle />
        <button onClick={load} style={{ padding: '8px 14px', background: NAVY_SOLID, color: IVORY_SOLID, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }} data-testid="refresh-btn">Refresh</button>
      </Header>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 12 }}>
        <Stat label="Leads in window" value={funnel.total_leads_in_window} accent={NAVY_SOLID} />
        <Stat label="Pipeline (weighted 90d)" value={`$${Math.round(rev.pipeline_weighted).toLocaleString()}`} accent="var(--admin-gold-solid)" />
        <Stat label="Bound YTD (comm.)" value={`$${rev.bound_actual_ytd.toLocaleString()}`} accent={NAVY_SOLID} />
        <Stat label="Compliance rejections 30d" value={comp.rejected_saves} accent="#991B1B" />
      </div>

      <Card title="Funnel — aggregate all sources">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            ['new', 'New', totals.new],
            ['contacted', 'Contacted', totals.contacted],
            ['qualified', 'Qualified', totals.qualified],
            ['in_consultation', 'In Consultation', totals.in_consultation],
            ['bound_issued', 'Bound / Issued', totals.bound_issued],
            ['client', 'Client', totals.client],
          ].map(([k, label, v]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 140, fontSize: 13, color: SLATE }}>{label}</div>
              <div style={{ flex: 1, background: 'var(--admin-scrim-hover)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ background: NAVY_SOLID, height: 24, width: `${(v / maxFunnel) * 100}%`, transition: 'width 300ms ease' }} />
              </div>
              <div style={{ width: 60, textAlign: 'right', fontFamily: 'Fraunces,serif', fontSize: 16, color: NAVY }}>{v}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Source ROI (leads-by-source + revenue attribution)">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: SCRIM }}>
              <th style={th}>Source</th>
              <th style={thR}>Leads</th>
              <th style={thR}>Avg score</th>
              <th style={thR}>Qual rate</th>
              <th style={thR}>Client rate</th>
            </tr>
          </thead>
          <tbody>
            {(source?.by_source || []).sort((a, b) => b.count - a.count).map(s => (
              <tr key={s.source_prefix}>
                <td style={td}>{s.source_prefix}</td>
                <td style={tdR}>{s.count}</td>
                <td style={tdR}>{s.avg_score}</td>
                <td style={tdR}>{(s.qualified_rate * 100).toFixed(1)}%</td>
                <td style={tdR}>{(s.client_rate * 100).toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="Revenue projection — advisor breakdown">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: SCRIM }}>
              <th style={th}>Advisor</th>
              <th style={thR}>Bound YTD</th>
              <th style={thR}>Booked pending</th>
              <th style={thR}>Pipeline (weighted)</th>
            </tr>
          </thead>
          <tbody>
            {(rev.by_advisor || []).map(a => (
              <tr key={a.slug}>
                <td style={td}>{a.slug}</td>
                <td style={tdR}>${a.bound.toLocaleString()}</td>
                <td style={tdR}>${a.booked_pending.toLocaleString()}</td>
                <td style={tdR}>${a.pipeline_weighted.toLocaleString()}</td>
              </tr>
            ))}
            {!(rev.by_advisor || []).length && <tr><td colSpan={4} style={{ ...td, color: MUTED }}>No advisor data.</td></tr>}
          </tbody>
        </table>
        <div style={{ fontSize: 11, color: MUTED, marginTop: 8 }}>
          Renewals expected in horizon: ${rev.renewals_expected.toLocaleString()} · Total projected: ${rev.total_projected.toLocaleString()}
        </div>
      </Card>
    </div>
  )
}
const th = { textAlign: 'left', padding: 8, borderBottom: `1px solid ${BORDER}`, fontSize: 11, color: MUTED, fontWeight: 500, textTransform: 'uppercase' }
const thR = { ...th, textAlign: 'right' }
const td = { padding: 8, borderBottom: `1px solid ${BORDER_SOFT}` }
const tdR = { ...td, textAlign: 'right' }
